"use client";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { BadgePercent, Edit3Icon, Plus, Trash2, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useDiscountType } from "../context/DiscountTypeContext";

/* ================= MAIN PAGE ================= */

export default function DiscountType() {
  const {
  discountTypes,
  loading,
  addDiscountType,
  updateDiscountType,
  toggleStatus,
  deleteDiscountType,
} = useDiscountType();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  /* ---------- FILTER & SORT ---------- */
  const filtered = useMemo(() => {
    let data = discountTypes.filter((d) =>
      d.name.toLowerCase().includes(search.toLowerCase())
    );

    if (sortBy === "az") data.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "za") data.sort((a, b) => b.name.localeCompare(a.name));
    if (sortBy === "status") data.sort((a, b) =>
      a.status.localeCompare(b.status)
    );

    return data;
  }, [discountTypes, search, sortBy]);

  /* ---------- ACTIONS ---------- */
 const save = async () => {
  if (!name.trim()) return;

  if (editing) {
    await updateDiscountType(editing.id, name);
  } else {
    await addDiscountType(name);
  }

  setModalOpen(false);
  setEditing(null);
  setName("");
};


 const toggleStatusHandler = (id) => {
  toggleStatus(id);
};


const removeHandler = (id) => {
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this discount type?"
  );

  if (confirmDelete) {
    deleteDiscountType(id);
  }
};

  /* ================= UI ================= */

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
     <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
  <div className="flex items-center gap-4">
    <div className="w-12 h-12 bg-teal-700 rounded-xl flex items-center justify-center shadow-sm">
      <BadgePercent className="w-6 h-6 text-white" />
    </div>

   <div>
  <h1 className="text-2xl sm:text-3xl font-bold text-teal-800 leading-tight">
    Discount Types
  </h1>
  <p className="mt-1 text-gray-600">
    Manage your discount categories
  </p>
</div>
  </div>

  <button
    onClick={() => setModalOpen(true)}
      className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
          >
    <Plus className="w-5 h-5" />
    Add Discount Type
  </button>
</div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Total" value={discountTypes.length} />
        <Stat
          label="Active"
          value={discountTypes.filter((d) => d.status === "Active").length}
          color="text-green-600"
        />
        <Stat
          label="Inactive"
          value={discountTypes.filter((d) => d.status === "Inactive").length}
          color="text-red-600"
        />
      </div>

      {/* Search & Sort */}
      <div className="flex flex-col md:flex-row gap-4 bg-orange-50 border border-orange-200 rounded-xl p-4">
        <input
          placeholder="Search discount type..."
          className="px-4 py-2 border rounded-xl w-full md:w-80"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="px-4 py-2 border rounded-xl w-full md:w-52"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="">Sort By</option>
          <option value="az">Name A → Z</option>
          <option value="za">Name Z → A</option>
         
        </select>
      </div>

      {/* Desktop Table */}
    <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
  <tr>
    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
      ID
    </th>

    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
      Discount Type
    </th>

    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
      Actions
    </th>
  </tr>
</thead>


    {/* ---------- TABLE BODY ---------- */}
    <tbody>
  {filtered.map((item) => (
    <tr
      key={item.id}
       className="border-t hover:bg-gray-50"
    >
      {/* ID */}
      <td className="px-4 py-2 md:table-cell md:px-6 md:py-4">
        <span className="font-semibold text-gray-600 md:hidden">ID</span>
        <span>{item.id}</span>
      </td>

      {/* Discount Name */}
      <td className="px-4 py-2 md:table-cell md:px-6 md:py-4">
        {/* <span className="font-semibold text-gray-600 md:hidden">
          Discount
        </span> */}
        <span className="font-medium">{item.name}</span>
      </td>

      {/* Status */}
     

      {/* Actions */}
      <td className="px-4 py-3 md:table-cell md:px-6 md:py-4">
        <div className="flex justify-end">
          <Actions
            item={item}
            onEdit={() => {
              setEditing(item);
              setName(item.name);
              setModalOpen(true);
            }}
           onToggle={toggleStatusHandler}
         onDelete={removeHandler}
          />
        </div>
      </td>
    </tr>
  ))}
</tbody>

  </table>
</div>

      {/* Mobile Cards */}
     <div className="md:hidden space-y-4">
  {filtered.map((item) => (
    <div
      key={item.id}
      className="bg-white rounded-xl shadow p-4 space-y-2"
    >
            <div className="flex justify-between">
              <div>
                <p className="text-xs text-gray-500">ID</p>
                <p>{item.id}</p>
              </div>
              <Actions
                item={item}
                onEdit={() => {
                  setEditing(item);
                  setName(item.name);
                  setModalOpen(true);
                }}
               onToggle={toggleStatusHandler}
           onDelete={removeHandler}
              />
            </div>

            <div>
              <p className="text-xs text-gray-500">Discount Type</p>
              <p className="font-medium">{item.name}</p>
            </div>

            <StatusBadge status={item.status} />
          </div>
        ))}
      </div>

      {/* Modal */}
     {modalOpen &&
  createPortal(
    <>
      {/* BACKDROP */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-md z-[1000]"
        onClick={() => setModalOpen(false)}
      />

      {/* MODAL */}
      <div className="fixed inset-0 z-[1010] flex items-start justify-end ">
        <div
          className="w-full max-w-md min-h-screen bg-gradient-to-br from-orange-50 to-orange-200 rounded-2xl shadow-2xl p-6 relative animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close */}
          <button
            onClick={() => setModalOpen(false)}
            className="absolute top-4 right-4 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center"
          >
            <X size={18} />
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-teal-800">
              {editing ? "Edit Discount Type" : "Add Discount Type"}
            </h2>
            <div className="w-16 h-1 bg-teal-700 rounded-full mx-auto mt-2" />
          </div>

          {/* Input */}
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Discount type name"
            className="w-full px-4 py-3 border rounded-xl mb-6"
          />

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={() => setModalOpen(false)}
              className="flex-1 px-4 py-3 bg-white border rounded-xl"
            >
              Cancel
            </button>

            <button
              onClick={save}
              className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-xl"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  )}



    </div>
  );
}

/* ================= COMPONENTS ================= */

const Stat = ({ label, value, color = "text-gray-900" }) => (
  <div className="bg-white border border-orange-100 rounded-xl p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const StatusBadge = ({ status }) => (
  <span
    className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
      status === "Active"
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700"
    }`}
  >
    {status}
  </span>
);

const Actions = ({ item, onEdit, onToggle, onDelete }) => {
  return (
    <div className="flex items-center gap-3 justify-end">
      {/* Status Toggle */}
      <button
        onClick={() => onToggle(item.id)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
          item.status === "Active" ? "bg-green-600" : "bg-gray-300"
        }`}
        title={item.status === "Active" ? "Inactivate" : "Activate"}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            item.status === "Active" ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>

      {/* Edit */}
      <button
        onClick={onEdit}
        className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors"
        title="Edit"
      >
        <Edit3Icon size={16} />
      </button>

      {/* Delete */}
      <button
        onClick={() => onDelete(item.id)}
        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
        title="Delete"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};


const MenuItem = ({ children, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`w-full px-4 py-2 text-left ${
      danger
        ? "text-red-600 hover:bg-red-50"
        : "hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);
